import { Check, ExternalLink, MapPin, Trash2 } from "lucide-react";

import { DescripcionDelAviso } from "@/components/referencias/DescripcionDelAviso";
import { Badge } from "@/components/ui/badge";
import {
  describirDesvio,
  describirPrecision,
  desvioContraLaMediana,
  DESVIO_QUE_ES_RUIDO,
  distancia,
  fechaCorta,
  hectareas,
  numero,
  pesos,
  valorUnitarioEnPesos,
} from "@/lib/formatoDeMercado";
import { describirClaseDeSuelo, type Comparable } from "@/lib/referencias";
import { cn } from "@/lib/utils";

interface FichaDeComparableProps {
  ficha: Comparable;
  /** Su lugar en el listado. Ancla la conversación: "la 7 no me cuadra". */
  numeroDeOrden: number;
  seleccionada: boolean;
  onAlternar: () => void;
  /** La mediana de UF/ha del filtro: contra ella se dibuja la barra. */
  medianaUfHa: number | null;
  /** El máximo del filtro, que fija la escala de la barra. */
  maximoUfHa: number | null;
  /** "Completa" agrega el pie con la procedencia; "compacta" lo omite. */
  /** Si el tasador ya revisó los datos de esta ficha y los dio por buenos. */
  validada: boolean;
  onValidar: (validada: boolean) => void;
  onDescartar: () => void;
  /** Hay una decisión en vuelo: los dos botones se apagan hasta que vuelva. */
  decidiendo?: boolean;
}

/**
 * Una ficha del catálogo de mercado.
 *
 * EL CUERPO ES UNA TABLA CHICA, NO UNA GRILLA CON ESPACIOS, y esa es la
 * diferencia que más se ve. La versión anterior separaba las métricas con
 * `gap` y metía el valor unitario en una caja verde con esquinas redondeadas;
 * el diseño las separa con LÍNEAS DE 1 PX y deja el valor unitario como una
 * columna más, distinguida por su fondo y su borde izquierdo. Con espacios la
 * ficha se lee como cuatro bloques sueltos; con líneas, como un solo dato en
 * cuatro columnas, que es lo que es.
 *
 * LAS MEDIDAS VAN EN `rem` Y NO EN PX aunque el diseño esté en píxeles: la
 * densidad de toda la aplicación la gobierna una sola perilla
 * (`html { font-size: 93.75% }`) y un px fijo se le escapa, dejando esta
 * pantalla desalineada con el resto justo en los equipos con la letra al 150 %.
 * La conversión es sobre la base de 16 px del diseño.
 *
 * TODA LA FICHA ALTERNA LA SELECCIÓN, no solo la casilla. Son objetivos de
 * 19 px en una grilla que se recorre rápido, y errarle a la casilla y abrir
 * otra cosa es la molestia que más se repite en un listado así.
 */
export function FichaDeComparable({
  ficha,
  numeroDeOrden,
  seleccionada,
  onAlternar,
  medianaUfHa,
  maximoUfHa,
  validada,
  onValidar,
  onDescartar,
  decidiendo = false,
}: FichaDeComparableProps) {
  const desvio = desvioContraLaMediana(ficha.uf_ha_terreno, medianaUfHa);
  const esCaro = desvio !== null && desvio >= DESVIO_QUE_ES_RUIDO;
  const esBarato = desvio !== null && desvio <= -DESVIO_QUE_ES_RUIDO;

  const valorUnitarioPesos = valorUnitarioEnPesos(ficha);
  const claseDeSuelo = describirClaseDeSuelo(ficha.clase_suelo_detalle);

  const viejo = ficha.dias_en_mercado !== null && ficha.dias_en_mercado > 90;

  return (
    <article
      onClick={onAlternar}
      className={cn(
        "cursor-pointer overflow-hidden rounded-[1rem] border-[1.5px] bg-card transition-[border-color,box-shadow]",
        seleccionada
          ? "border-primary shadow-[0_0_0_3px_color-mix(in_oklab,var(--color-primary)_18%,transparent)]"
          : "border-border hover:border-ink-ghost",
      )}
    >
      <div className="px-[0.875rem] pt-[0.75rem] pb-[0.625rem]">
        {/* Fila 1: de dónde salió y a qué distancia está. */}
        <div className="flex flex-wrap items-center gap-[0.5625rem]">
          <span className="text-ink-hint font-mono text-[0.6875rem] font-semibold tabular-nums">
            {numeroDeOrden}
          </span>

          <Badge variant={ficha.clase === "transaccion" ? "info" : "warning"}>
            {ficha.clase === "transaccion" ? "TRANSACCIÓN CBR" : "OFERTA"}
          </Badge>

          {ficha.referencia && (
            <span className="text-ink-muted font-mono text-[0.75rem] font-semibold">
              {ficha.clase === "transaccion" ? `Rol ${ficha.referencia}` : ficha.referencia}
            </span>
          )}

          {ficha.distancia_m !== null && (
            <Chip>
              <MapPin className="size-[0.6875rem]" />
              {distancia(ficha.distancia_m)}
            </Chip>
          )}

          {/* Las dos decisiones que se toman SIN salir del listado: dar los
              datos por buenos y sacar la ficha del análisis.

              `ml-auto` con `flex-none` sobre una fila que envuelve: en pantalla
              angosta bajan juntas a su propio renglón en vez de apretujar los
              chips de la izquierda. */}
          <div className="ml-auto flex flex-none items-center gap-[0.5rem]">
            <BotonValidar
              validada={validada}
              titulo={ficha.titulo}
              deshabilitado={decidiendo}
              onValidar={() => onValidar(!validada)}
            />
            <BotonDescartar
              titulo={ficha.titulo}
              deshabilitado={decidiendo}
              onDescartar={onDescartar}
            />
          </div>
        </div>

        {/* Fila 2: la casilla y el título.

            La casilla se dibuja a mano y no con el `Checkbox` del DS: el diseño
            la quiere de 19 px con borde de 1.5 px y radio 6, y el del DS mide
            20 con otro radio. Es un control de SOLO LECTURA visual — quien
            alterna es la ficha entera—, así que no se pierde ningún
            comportamiento. */}
        <div className="mt-[0.5625rem] flex items-start gap-[0.6875rem]">
          <span
            role="checkbox"
            aria-checked={seleccionada}
            aria-label={ficha.titulo}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === " " || e.key === "Enter") {
                e.preventDefault();
                onAlternar();
              }
            }}
            className={cn(
              "mt-[0.125rem] flex size-[1.1875rem] flex-none items-center justify-center rounded-[0.375rem] border-[1.5px] transition-colors",
              seleccionada ? "border-primary bg-primary" : "border-input bg-background dark:bg-input/30",
            )}
          >
            <svg viewBox="0 0 12 12" className="size-[0.625rem]" aria-hidden>
              <path
                d="M2.5 6.2 4.8 8.5 9.5 3.8"
                fill="none"
                stroke="#fff"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity={seleccionada ? 1 : 0}
              />
            </svg>
          </span>

          <span className="text-ink text-[0.875rem] leading-[1.3] font-bold tracking-[-0.01em]">
            {ficha.titulo}
          </span>
        </div>

        {/* Fila 3: dónde, cuándo y cuánto confiar en la ubicación.

            Sangrada 30px para alinear con el título, no con la casilla. */}
        <div className="mt-[0.375rem] flex flex-wrap items-center gap-[0.5625rem] pl-[1.875rem]">
          <span className="text-ink-body text-[0.78125rem] font-bold">
            {ficha.comuna || "Comuna sin informar"}
          </span>

          {/* VA ACÁ Y NO EN EL `<dl>` DE MÉTRICAS: aquel bloque es
              `font-mono tabular-nums`, pensado para cifras que se comparan en
              columna, y un código como "IIIr" ahí queda desalineado y sugiere
              que es una magnitud. La clase de suelo es un atributo del
              TERRITORIO, igual que la comuna con la que comparte fila. */}
          {claseDeSuelo && (
            <>
              <span className="text-ink-ghost">·</span>
              <span className="text-ink-muted text-[0.75rem] font-semibold">{claseDeSuelo}</span>
            </>
          )}

          <span className="text-ink-ghost">·</span>
          <span className="text-ink-muted font-mono text-[0.75rem] font-semibold">
            {fechaCorta(ficha.fecha)}
          </span>

          {ficha.clase === "transaccion" ? (
            <Chip>Inscrita</Chip>
          ) : (
            ficha.dias_en_mercado !== null && (
              <Chip tono={viejo ? "alerta" : "neutro"}>{ficha.dias_en_mercado} días en mercado</Chip>
            )
          )}

          <Chip tono={ficha.ubicacion_estimada ? "alerta" : "ok"}>
            <span
              className={cn(
                "size-[0.5625rem] flex-none rounded-full border-[1.5px]",
                ficha.ubicacion_estimada
                  ? "border-warning-dot border-dashed bg-transparent"
                  : "border-primary bg-transparent",
              )}
            />
            {describirPrecision(ficha.precision_ubicacion)}
          </Chip>
        </div>
      </div>

      {/* El cuerpo: cuatro columnas separadas por LÍNEAS, no por espacios. */}
      <div className="border-divisor grid border-t md:grid-cols-[minmax(0,1fr)_12.875rem]">
        <dl className="grid min-w-0 grid-cols-[repeat(auto-fit,minmax(7.875rem,1fr))]">
          <Metrica rotulo="Superficie ha" valor={hectareas(ficha.superficie_terreno_m2)} />
          <Metrica rotulo="Valor total $" valor={pesos(ficha.precio_monto)} />
          <Metrica rotulo="Valor unit. $/ha" valor={pesos(valorUnitarioPesos)} />
        </dl>

        <div
          className={cn(
            "border-divisor min-w-0 border-l px-[0.8125rem] pt-[0.5625rem] pb-[0.625rem]",
            esCaro ? "bg-warning-soft/50" : "bg-success-soft/60",
          )}
        >
          <div className="flex items-baseline gap-[0.5rem]">
            <span className="text-ink-muted text-[0.59375rem] font-extrabold tracking-[0.05em] uppercase">
              Valor unitario UF/ha
            </span>
            <span
              className={cn(
                "ml-auto text-[0.6875rem] font-extrabold",
                esCaro && "text-warning-soft-foreground",
                esBarato && "text-success-soft-foreground",
                !esCaro && !esBarato && "text-ink-hint",
              )}
            >
              {describirDesvio(desvio)}
            </span>
          </div>

          <div className="mt-[0.125rem] flex items-baseline gap-[0.5625rem]">
            <span className="text-ink font-mono text-[1.125rem] font-bold tracking-[-0.02em] tabular-nums">
              {numero(ficha.uf_ha_terreno, 0)}
            </span>
            <span className="text-ink-hint font-mono text-[0.6875rem] font-bold">
              UF {numero(ficha.precio_uf, 0)} total
            </span>
          </div>

          <BarraContraLaMediana
            valor={ficha.uf_ha_terreno}
            mediana={medianaUfHa}
            maximo={maximoUfHa}
            esCaro={esCaro}
          />
        </div>
      </div>

      {/* La prosa del aviso, entre las cifras y la procedencia — que es donde
          cae en el aviso original: primero cuánto y cuánto mide, después qué
          dice el vendedor, al final de dónde salió.

          VA EN LAS DOS DENSIDADES, al revés que el pie. "Compacta" existe para
          que entren más fichas en pantalla y por eso se le saca la procedencia,
          que es metadata; la descripción es lo que hace que una ficha se pueda
          descartar sin abrirla, así que sacarla obligaría a volver a la densidad
          completa para leer lo que se está recorriendo. Recortada a dos líneas
          cuesta lo mismo en las dos. */}
      <DescripcionDelAviso
        descripcion={ficha.descripcion}
        className="border-divisor border-t px-[0.875rem] pt-[0.5rem] pb-[0.5625rem]"
      />

      {/* LA PROCEDENCIA VA SIEMPRE, desde que se retiraron los dos botones de
          densidad (09-09-2026). Era lo único que distinguía "compacta" de
          "completa", y con la elección fuera un condicional sobre un valor que
          ya no puede cambiar es código que no se ejecuta nunca de una de las dos
          formas. */}
      <div className="border-divisor text-ink-hint flex flex-wrap items-center justify-between gap-2 border-t px-[0.875rem] py-[0.5rem] text-[0.6875rem]">
        <span className="font-mono font-semibold">
          {ficha.fuente}
          {ficha.comuna_codigo && <> · {ficha.comuna_codigo}</>}
        </span>

        {ficha.url ? (
          <a
            href={ficha.url}
            target="_blank"
            rel="noreferrer noopener"
            onClick={(evento) => evento.stopPropagation()}
            className="text-primary inline-flex max-w-full items-center gap-1 truncate hover:underline"
          >
            <ExternalLink className="size-3 shrink-0" />
            <span className="truncate">{ficha.url}</span>
          </a>
        ) : (
          <span>Sin aviso público (registro CBR)</span>
        )}
      </div>
    </article>
  );
}

/**
 * El interruptor de "ya revisé estos datos".
 *
 * DICE EL ESTADO Y NO LA ACCIÓN —"Validada" con el check puesto, no
 * "Validar"— porque es un interruptor: lo que se lee es lo que la ficha ES, y
 * volver a apretarlo lo deshace. Mismo criterio que en `FichaDeReferencia`.
 *
 * SE DIBUJA A MANO Y NO CON EL `Button` DEL DS, igual que la casilla de esta
 * misma tarjeta y por el mismo motivo: el más chico del DS mide 36 px de alto y
 * el diseño pide 29, que es lo que deja los dos controles dentro del renglón de
 * chips en vez de estirarlo. Los colores sí son todos del DS —el diseño se armó
 * sobre la misma paleta—, así que el tema oscuro lo acompaña sin nada especial.
 *
 * El `aria-label` EMPIEZA por el texto visible: así el nombre accesible
 * distingue una ficha de otra en una lista de seis botones idénticos sin
 * contradecir lo que se lee en pantalla.
 */
function BotonValidar({
  validada,
  titulo,
  deshabilitado,
  onValidar,
}: {
  validada: boolean;
  titulo: string;
  deshabilitado: boolean;
  onValidar: () => void;
}) {
  const etiqueta = validada ? "Validada" : "Sin validar";

  return (
    <button
      type="button"
      // La tarjeta ENTERA alterna la selección. Sin esto, validar seleccionaría
      // además la ficha: dos cosas distintas en un clic, y la segunda sin que
      // nadie la pidiera.
      onClick={(evento) => {
        evento.stopPropagation();
        onValidar();
      }}
      disabled={deshabilitado}
      aria-pressed={validada}
      aria-label={`${etiqueta}: ${titulo}`}
      title={validada ? "Quitar la marca de dato validado" : "Marcar como dato validado"}
      className={cn(
        "inline-flex flex-none items-center gap-[0.4375rem] rounded-[0.5625rem] border-[1.5px] px-[0.625rem] py-[0.375rem] text-[0.71875rem] font-bold transition-colors",
        "disabled:cursor-not-allowed disabled:opacity-60",
        validada
          ? "border-[color-mix(in_oklab,var(--color-success-soft-foreground)_15%,var(--color-success-soft))] bg-success-soft text-success-soft-foreground"
          : "border-border bg-card text-ink-hint hover:border-ink-ghost hover:text-ink-muted",
      )}
    >
      {/* `stroke-[3]`: el check del diseño es más grueso que el resto de los
          íconos, para que se lea a 12 px. */}
      <Check className="size-[0.75rem] stroke-[3]" aria-hidden />
      {etiqueta}
    </button>
  );
}

/**
 * Sacar la ficha del análisis, sin borrarla de ningún lado.
 *
 * ES ROJO SOLO AL PASAR POR ENCIMA. En reposo es un botón neutro más, porque lo
 * que hace es reversible —va a la papelera del informe— y un ícono rojo
 * permanente en cada una de las seis fichas leería la pantalla como un campo
 * minado. El rojo aparece cuando el puntero ya está encima, que es cuando sirve
 * de advertencia.
 */
function BotonDescartar({
  titulo,
  deshabilitado,
  onDescartar,
}: {
  titulo: string;
  deshabilitado: boolean;
  onDescartar: () => void;
}) {
  return (
    <button
      type="button"
      onClick={(evento) => {
        evento.stopPropagation();
        onDescartar();
      }}
      disabled={deshabilitado}
      aria-label={`Enviar a la papelera: ${titulo}`}
      title="Enviar a la papelera del informe"
      className={cn(
        "flex size-[1.875rem] flex-none items-center justify-center rounded-[0.5625rem] border-[1.5px] transition-colors",
        "border-border bg-card text-ink-hint",
        "hover:border-[color-mix(in_oklab,var(--color-danger-soft-foreground)_20%,var(--color-danger-soft))] hover:bg-danger-soft hover:text-danger-soft-foreground",
        "disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:border-border disabled:hover:bg-card disabled:hover:text-ink-hint",
      )}
    >
      <Trash2 className="size-[0.875rem]" aria-hidden />
    </button>
  );
}

/**
 * Los datos de contexto del diseño son CHIPS con fondo, no texto suelto.
 *
 * La diferencia importa: sobre una fila con cinco datos seguidos, el fondo es
 * lo que separa "Colbún" de "1169 días en mercado" sin necesidad de un
 * separador entre cada par.
 */
function Chip({
  children,
  tono = "neutro",
}: {
  children: React.ReactNode;
  tono?: "neutro" | "ok" | "alerta";
}) {
  return (
    <span
      className={cn(
        "inline-flex flex-none items-center gap-[0.375rem] rounded-[0.4375rem] px-[0.5rem] py-[0.1875rem] text-[0.6875rem] font-bold",
        tono === "alerta" && "bg-warning-soft text-warning-soft-foreground",
        tono === "ok" && "bg-success-soft text-success-soft-foreground",
        tono === "neutro" && "bg-muted text-ink-muted",
      )}
    >
      {children}
    </span>
  );
}

/**
 * Una celda del cuerpo, con sus divisores.
 *
 * `border-r` y `border-b` en cada celda y no un `gap` en la grilla: es lo que
 * arma la retícula del diseño. La última de cada fila hereda un borde de más,
 * que el `overflow-hidden` de la tarjeta recorta.
 */
function Metrica({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="border-divisor border-r border-b px-[0.75rem] py-[0.5625rem] last:border-r-0">
      <dt className="text-ink-hint text-[0.59375rem] leading-[1.3] font-extrabold tracking-[0.05em] uppercase">
        {rotulo}
      </dt>
      <dd className="text-ink mt-[0.25rem] font-mono text-[0.8125rem] leading-[1.25] font-bold tracking-[-0.01em] tabular-nums">
        {valor}
      </dd>
    </div>
  );
}

/**
 * La barra del valor unitario, con la mediana marcada.
 *
 * La ESCALA es el máximo del filtro y no el de la ficha: dos barras solo se
 * pueden comparar entre sí si miden contra lo mismo. Y la marca de la mediana
 * va como una línea vertical y no como un cambio de color de la pista, porque
 * lo que hay que poder leer de un vistazo es de qué lado cae cada ficha.
 *
 * Sin valor unitario no se dibuja nada. Una barra en cero diría que esta ficha
 * vale cero, cuando lo que pasa es que no se pudo calcular.
 */
function BarraContraLaMediana({
  valor,
  mediana,
  maximo,
  esCaro,
}: {
  valor: number | null;
  mediana: number | null;
  maximo: number | null;
  esCaro: boolean;
}) {
  if (valor === null || maximo === null || maximo <= 0) return null;

  const porcentaje = Math.min((valor / maximo) * 100, 100);
  const porcentajeMediana = mediana === null ? null : Math.min((mediana / maximo) * 100, 100);

  return (
    <div className="relative mt-[0.5rem] h-[0.3125rem] w-full rounded-full bg-[color-mix(in_oklab,var(--color-primary)_18%,transparent)]">
      <div
        className={cn("h-full rounded-full", esCaro ? "bg-warning-dot" : "bg-primary")}
        style={{ width: `${porcentaje}%` }}
      />

      {porcentajeMediana !== null && (
        <span
          title="Mediana del filtro"
          className="bg-ink absolute top-[-2px] h-[9px] w-[2px] rounded-full"
          style={{ left: `${porcentajeMediana}%` }}
        />
      )}
    </div>
  );
}
